import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

// 扩展dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// 设置中文语言
dayjs.locale('zh-cn');

/**
 * 时间转换工具类
 */
export class TimeUtils {
  /**
   * 将UTC时间转换为本地时间
   * @param utcTime UTC时间字符串或Date对象
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 本地时间字符串
   */
  static utcToLocal(utcTime: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    if (!utcTime) return '';
    
    try {
      return dayjs.utc(utcTime).local().format(format);
    } catch (error) {
      console.error('UTC to local time conversion error:', error);
      return '';
    }
  }

  /**
   * 将本地时间转换为UTC时间
   * @param localTime 本地时间字符串或Date对象
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns UTC时间字符串
   */
  static localToUtc(localTime: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    if (!localTime) return '';
    
    try {
      return dayjs(localTime).utc().format(format);
    } catch (error) {
      console.error('Local to UTC time conversion error:', error);
      return '';
    }
  }

  /**
   * 将UTC时间转换为dayjs对象（本地时间）
   * @param utcTime UTC时间字符串或Date对象
   * @returns dayjs对象
   */
  static utcToDayjs(utcTime: string | Date): dayjs.Dayjs | null {
    if (!utcTime) return null;
    
    try {
      return dayjs.utc(utcTime).local();
    } catch (error) {
      console.error('UTC to dayjs conversion error:', error);
      return null;
    }
  }

  /**
   * 格式化时间显示（支持自定义格式）
   * @param time 时间字符串或Date对象
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @param isUtc 是否为UTC时间，默认为true
   * @returns 格式化后的时间字符串
   */
  static formatTime(time: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss', isUtc: boolean = true): string {
    if (!time) return '';
    
    try {
      if (isUtc) {
        return dayjs.utc(time).local().format(format);
      } else {
        return dayjs(time).format(format);
      }
    } catch (error) {
      console.error('Time formatting error:', error);
      return '';
    }
  }

  /**
   * 格式化时间显示（友好格式）
   * @param time 时间字符串或Date对象
   * @param isUtc 是否为UTC时间，默认为true
   * @returns 友好格式的时间字符串
   */
  static formatFriendlyTime(time: string | Date, isUtc: boolean = true): string {
    if (!time) return '';
    
    try {
      const targetTime = isUtc ? dayjs.utc(time).local() : dayjs(time);
      const now = dayjs();
      const diffInMinutes = now.diff(targetTime, 'minute');
      const diffInHours = now.diff(targetTime, 'hour');
      const diffInDays = now.diff(targetTime, 'day');
      
      // 1分钟内
      if (diffInMinutes < 1) {
        return '刚刚';
      }
      // 1小时内
      else if (diffInMinutes < 60) {
        return `${diffInMinutes}分钟前`;
      }
      // 24小时内
      else if (diffInHours < 24) {
        return `${diffInHours}小时前`;
      }
      // 7天内
      else if (diffInDays < 7) {
        return `${diffInDays}天前`;
      }
      // 超过7天，显示具体日期
      else {
        return targetTime.format('MM-DD HH:mm');
      }
    } catch (error) {
      console.error('Friendly time formatting error:', error);
      return '';
    }
  }

  /**
   * 格式化持续时间
   * @param seconds 秒数
   * @returns 格式化后的持续时间字符串
   */
  static formatDuration(seconds: number): string {
    if (typeof seconds !== 'number' || seconds < 0) return '00:00';
    
    const absSeconds = Math.abs(seconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);
    const remainingSeconds = absSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes}:${remainingSeconds}`;
    } else if (minutes > 0) {
      if(minutes>9){
        return `${minutes}:${remainingSeconds}`;
      }
      return `0${minutes}:${remainingSeconds}`;
    } else {
      return `00:${remainingSeconds}`;
    }
  }

  /**
   * 获取当前本地时间
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 当前本地时间字符串
   */
  static now(format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs().format(format);
  }

  /**
   * 获取当前UTC时间
   * @param format 输出格式，默认为 'YYYY-MM-DD HH:mm:ss'
   * @returns 当前UTC时间字符串
   */
  static nowUtc(format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs().utc().format(format);
  }

  /**
   * 检查时间是否有效
   * @param time 时间字符串或Date对象
   * @returns 是否有效
   */
  static isValid(time: string | Date): boolean {
    if (!time) return false;
    return dayjs(time).isValid();
  }

  /**
   * 获取相对时间
   * @param time 时间字符串或Date对象
   * @param isUtc 是否为UTC时间，默认为true
   * @returns 相对时间字符串
   */
  static fromNow(time: string | Date, isUtc: boolean = true): string {
    if (!time) return '';
    
    try {
      if (isUtc) {
        return dayjs.utc(time).local().fromNow();
      } else {
        return dayjs(time).fromNow();
      }
    } catch (error) {
      console.error('Relative time formatting error:', error);
      return '';
    }
  }
}

// 导出常用的时间格式常量
export const TIME_FORMATS = {
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DATETIME_MINUTE: 'YYYY-MM-DD HH:mm',
  CHINESE_DATE: 'YYYY年MM月DD日',
  CHINESE_DATETIME: 'YYYY年MM月DD日 HH:mm:ss',
  CHINESE_TIME: 'HH时mm分',
  MONTH_DAY: 'MM-DD',
  MONTH_DAY_TIME: 'MM-DD HH:mm',
  ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
} as const;

// 导出便捷函数
export const {
  utcToLocal,
  localToUtc,
  utcToDayjs,
  formatTime,
  formatFriendlyTime,
  formatDuration,
  now,
  nowUtc,
  isValid,
  fromNow,
} = TimeUtils;